import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEO from "@/components/base/SEO";
import ScrollToTop from "@/components/base/ScrollToTop";
import { comics, categories } from "@/mocks/comics";
import ComicCard from "@/components/base/ComicCard";

const siteUrl = "https://hanmanzhijia.com";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<"popular" | "latest" | "rating">("popular");

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || slug;

  const filtered = useMemo(() => {
    const list = comics.filter((c) => c.category === slug);
    if (activeSort === "latest") {
      return [...list].sort((a, b) => b.updateTime.localeCompare(a.updateTime));
    }
    if (activeSort === "rating") {
      return [...list].sort((a, b) => b.rating - a.rating);
    }
    return [...list].sort((a, b) => parseInt(b.views.replace("万", "")) - parseInt(a.views.replace("万", "")));
  }, [slug, activeSort]);

  const categoryJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${categoryName}韩国漫画 - 韩漫之家`,
    description: `韩漫之家为您提供高清${categoryName}韩国漫画在线免费阅读，收录热门${categoryName}韩漫作品，每日更新，品质保障。`,
    url: `${siteUrl}/category/${slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "韩漫之家",
      url: siteUrl,
    },
  };

  return (
    <>
      <SEO
        title={`${categoryName}韩国漫画_${categoryName}韩漫在线免费阅读-韩漫之家`}
        description={`韩漫之家为您提供高清${categoryName}韩国漫画在线免费阅读，收录热门${categoryName}韩漫作品，每日更新，品质保障。在韩漫之家享受${categoryName}韩漫阅读的可靠服务。`}
        keywords={`${categoryName}韩国漫画,${categoryName}韩漫,韩漫在线免费阅读,韩漫之家,高清韩漫大全`}
        canonicalUrl={`${siteUrl}/category/${slug}`}
        jsonLd={categoryJsonLd}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "分类浏览", href: "/category/love" },
            { label: `${categoryName}漫画` },
          ]}
        />

        <section className="py-8 md:py-12 px-4 md:px-6 max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {categoryName}韩国漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">共 {filtered.length} 部作品，每日更新，品质保障</p>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <aside className="md:w-48 flex-shrink-0">
              <nav className="flex md:flex-col gap-1 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                      cat.slug === slug
                        ? "bg-primary-100 text-primary-700 font-medium"
                        : "text-foreground-600 hover:bg-background-100 hover:text-primary-500"
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </nav>
            </aside>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-6">
                <span className="text-xs text-foreground-500 mr-2">排序：</span>
                {[
                  { key: "popular", label: "人气" },
                  { key: "latest", label: "最新" },
                  { key: "rating", label: "评分" },
                ].map((sort) => (
                  <button
                    key={sort.key}
                    onClick={() => setActiveSort(sort.key as typeof activeSort)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeSort === sort.key
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {sort.label}
                  </button>
                ))}
              </div>

              {filtered.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filtered.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
                  <p className="text-foreground-500">该分类暂无漫画</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}